import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CheckCircle2, XCircle, Undo2, ShieldCheck, Wallet, Users, RefreshCw, Plus, ExternalLink, Bot, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { formatDate, formatIDR, formatApiError } from "../lib/format";
import { useAuth } from "../context/AuthContext";

const initialRefundForm = { booking_id: "", payment_id: "", amount: "", reason: "", method: "Transfer", request_date: new Date().toISOString().slice(0, 10) };

export default function Approvals() {
  const { user } = useAuth();
  const isOwnerGM = user && ["owner", "general_manager"].includes(user.role);

  const [tab, setTab] = useState("payments");
  const [payments, setPayments] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [wages, setWages] = useState([]);
  const [agentPending, setAgentPending] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState({ payments_unverified: 0, refunds_pending: 0, wages_pending: 0, agent_pending: 0 });
  const [busyId, setBusyId] = useState(null);

  // Modal state: action confirmation with note
  const [actionDlg, setActionDlg] = useState(null); // {kind: "verify_payment"|"approve_wage"|"approve_refund"|"reject_refund"|"reject_wage"|"unverify_payment", id, label, warn?}
  const [note, setNote] = useState("");

  // Modal state: create refund
  const [refundOpen, setRefundOpen] = useState(false);
  const [rForm, setRForm] = useState(initialRefundForm);

  const reload = async () => {
    try {
      const [{ data: p }, { data: r }, { data: w }, { data: s }, { data: b }, agentRes] = await Promise.all([
        api.get("/payments"),
        api.get("/refunds"),
        api.get("/job-wages"),
        api.get("/approvals/summary"),
        api.get("/bookings"),
        isOwnerGM ? api.get("/agent-pending-changes") : Promise.resolve({ data: [] }),
      ]);
      setPayments(p); setRefunds(r); setWages(w); setSummary(s); setBookings(b);
      setAgentPending(agentRes.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal memuat data"); }
  };
  useEffect(() => { reload(); }, []);

  const unverifiedPayments = useMemo(() => payments.filter((p) => !p.verified && Number(p.amount) > 0)
    .sort((a, b) => (b.payment_date || "").localeCompare(a.payment_date || "")), [payments]);
  const pendingRefunds = useMemo(() => refunds.filter((r) => r.status === "Pending"), [refunds]);
  const historyRefunds = useMemo(() => refunds.filter((r) => r.status !== "Pending").slice(0, 30), [refunds]);
  const pendingWages = useMemo(() => wages.filter((w) => w.status === "Pending")
    .sort((a, b) => (b.job_date || "").localeCompare(a.job_date || "")), [wages]);

  const openAction = (kind, id, label, warn) => {
    setActionDlg({ kind, id, label, warn });
    setNote("");
  };

  const submitAction = async () => {
    if (!actionDlg) return;
    setBusyId(actionDlg.id);
    const { kind, id } = actionDlg;
    const payload = { note: note.trim() };
    const routeMap = {
      verify_payment: `/payments/${id}/verify`,
      unverify_payment: `/payments/${id}/unverify`,
      approve_wage: `/job-wages/${id}/approve`,
      reject_wage: `/job-wages/${id}/reject`,
      approve_refund: `/refunds/${id}/approve`,
      reject_refund: `/refunds/${id}/reject`,
      approve_agent: `/agent-pending-changes/${id}/approve`,
      reject_agent: `/agent-pending-changes/${id}/reject`,
    };
    try {
      await api.post(routeMap[kind], payload);
      toast.success("Berhasil diproses");
      setActionDlg(null);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal memproses");
    } finally { setBusyId(null); }
  };

  const submitRefund = async () => {
    if (!rForm.booking_id || !rForm.amount) { toast.error("Pilih booking & isi nominal"); return; }
    try {
      await api.post("/refunds", {
        booking_id: rForm.booking_id,
        payment_id: rForm.payment_id || null,
        amount: Number(rForm.amount),
        reason: rForm.reason, method: rForm.method,
        request_date: rForm.request_date,
      });
      toast.success("Pengajuan refund dibuat");
      setRefundOpen(false); setRForm(initialRefundForm);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Gagal membuat refund"); }
  };

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Persetujuan Masuk</h1>
          <p className="text-sm text-[#5C6066] mt-1">Verifikasi pembayaran klien, refund, dan fee crew sebelum dieksekusi.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={reload} data-testid="btn-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Muat Ulang</Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid="sum-payments">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold"><Wallet className="w-3 h-3"/> Pembayaran Belum Diverifikasi</div>
          <div className="font-display text-2xl font-bold mt-1">{summary.payments_unverified}</div>
        </Card>
        <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid="sum-refunds">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold"><Undo2 className="w-3 h-3"/> Refund Menunggu Persetujuan</div>
          <div className="font-display text-2xl font-bold mt-1">{summary.refunds_pending}</div>
        </Card>
        <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid="sum-wages">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold"><Users className="w-3 h-3"/> Fee Crew Menunggu Persetujuan</div>
          <div className="font-display text-2xl font-bold mt-1">{summary.wages_pending}</div>
        </Card>
        <Card className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid="sum-agent">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold"><Bot className="w-3 h-3"/> AI Agent Menunggu Persetujuan</div>
          <div className="font-display text-2xl font-bold mt-1">{summary.agent_pending || 0}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList data-testid="approval-tabs">
          <TabsTrigger value="payments" data-testid="tab-payments">Pembayaran ({summary.payments_unverified})</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">Refund ({summary.refunds_pending})</TabsTrigger>
          <TabsTrigger value="wages" data-testid="tab-wages">Fee Crew ({summary.wages_pending})</TabsTrigger>
          <TabsTrigger value="agent" data-testid="tab-agent">AI Agent ({summary.agent_pending || 0})</TabsTrigger>
        </TabsList>

        {/* Pembayaran */}
        <TabsContent value="payments" className="mt-0">
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-5 py-3">Tanggal</th>
                    <th className="text-left px-4 py-3">Booking</th>
                    <th className="text-left px-4 py-3">Klien</th>
                    <th className="text-left px-4 py-3">Tipe</th>
                    <th className="text-right px-4 py-3">Nominal</th>
                    <th className="text-left px-4 py-3">Metode</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {unverifiedPayments.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8C9096]">Semua pembayaran sudah diverifikasi ✅</td></tr>}
                  {unverifiedPayments.slice(0, 200).map((p) => (
                    <tr key={p.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`pay-row-${p.id}`}>
                      <td className="px-5 py-3 text-xs">{formatDate(p.payment_date)}</td>
                      <td className="px-4 py-3"><Link to={`/bookings/${p.booking_id}`} className="font-mono text-xs text-brand hover:underline inline-flex items-center gap-1">{p.booking_code || p.booking_id.slice(0,8)}<ExternalLink className="w-3 h-3"/></Link></td>
                      <td className="px-4 py-3 text-xs">{p.client_name || "-"}</td>
                      <td className="px-4 py-3 text-xs">{p.payment_type}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatIDR(p.amount)}</td>
                      <td className="px-4 py-3 text-xs">{p.method || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                          disabled={busyId === p.id || !isOwnerGM}
                          onClick={() => openAction("verify_payment", p.id, `Verifikasi pembayaran ${p.payment_type} — ${formatIDR(p.amount)}`)}
                          data-testid={`btn-verify-${p.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5 mr-1"/>Verifikasi
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Refund */}
        <TabsContent value="refunds" className="mt-0 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display font-semibold text-sm">Pengajuan Refund</h2>
            <Button size="sm" className="bg-brand text-white h-8" onClick={() => setRefundOpen(true)} data-testid="btn-new-refund">
              <Plus className="w-4 h-4 mr-1"/>Ajukan Refund Baru
            </Button>
          </div>
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-5 py-3">Tgl Pengajuan</th>
                    <th className="text-left px-4 py-3">Booking</th>
                    <th className="text-left px-4 py-3">Klien</th>
                    <th className="text-right px-4 py-3">Nominal</th>
                    <th className="text-left px-4 py-3">Alasan</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingRefunds.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Belum ada pengajuan refund menunggu persetujuan.</td></tr>}
                  {pendingRefunds.map((r) => (
                    <tr key={r.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`refund-row-${r.id}`}>
                      <td className="px-5 py-3 text-xs">{formatDate(r.request_date)}</td>
                      <td className="px-4 py-3"><Link to={`/bookings/${r.booking_id}`} className="font-mono text-xs text-brand hover:underline inline-flex items-center gap-1">{r.booking_code || "-"}<ExternalLink className="w-3 h-3"/></Link></td>
                      <td className="px-4 py-3 text-xs">{r.client_name || "-"}</td>
                      <td className="px-4 py-3 text-right font-mono text-rose-600">- {formatIDR(r.amount)}</td>
                      <td className="px-4 py-3 text-xs max-w-xs truncate" title={r.reason}>{r.reason || "-"}</td>
                      <td className="px-4 py-3 text-right space-x-1.5">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" disabled={!isOwnerGM || busyId === r.id}
                          onClick={() => openAction("approve_refund", r.id, `Setujui refund Rp.${new Intl.NumberFormat("id-ID").format(r.amount)}`,
                            "Akan mencatat pembayaran negatif otomatis di ledger booking.")}
                          data-testid={`btn-approve-refund-${r.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Setujui
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-600 hover:bg-rose-50" disabled={!isOwnerGM || busyId === r.id}
                          onClick={() => openAction("reject_refund", r.id, `Tolak refund Rp.${new Intl.NumberFormat("id-ID").format(r.amount)}`)}
                          data-testid={`btn-reject-refund-${r.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1"/>Tolak
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {historyRefunds.length > 0 && (
            <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
              <div className="px-5 py-2 text-[10px] uppercase tracking-wider text-[#8C9096] font-semibold bg-[#FAF6EE]">Riwayat Refund (30 terakhir)</div>
              <div className="divide-y divide-[#F0EBE0]">
                {historyRefunds.map((r) => (
                  <div key={r.id} className="px-5 py-2 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`px-1.5 py-0.5 rounded font-semibold ${r.status === "Approved" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.status}</span>
                      <span className="font-mono">{r.booking_code || "-"}</span>
                      <span className="truncate max-w-xs text-[#5C6066]">{r.client_name} — {r.reason || "-"}</span>
                    </div>
                    <span className="font-mono text-rose-600">- {formatIDR(r.amount)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* Wages */}
        <TabsContent value="wages" className="mt-0">
          <Card className="border border-[#E5E0D8] bg-white shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-[#8C9096] bg-[#FAF6EE]">
                    <th className="text-left px-5 py-3">Tgl Job</th>
                    <th className="text-left px-4 py-3">Booking</th>
                    <th className="text-left px-4 py-3">Nama</th>
                    <th className="text-left px-4 py-3">Peran</th>
                    <th className="text-right px-4 py-3">Nominal</th>
                    <th className="text-right px-4 py-3">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWages.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8C9096]">Tidak ada fee crew yang menunggu persetujuan.</td></tr>}
                  {pendingWages.map((w) => (
                    <tr key={w.id} className="border-t border-[#F0EBE0] hover:bg-[#F7F4EE]" data-testid={`wage-row-${w.id}`}>
                      <td className="px-5 py-3 text-xs">{formatDate(w.job_date)}</td>
                      <td className="px-4 py-3"><Link to={`/bookings/${w.booking_id}`} className="font-mono text-xs text-brand hover:underline inline-flex items-center gap-1">{w.booking_code || "-"}<ExternalLink className="w-3 h-3"/></Link></td>
                      <td className="px-4 py-3 text-xs">{w.staff_name}</td>
                      <td className="px-4 py-3 text-xs">{w.role}</td>
                      <td className="px-4 py-3 text-right font-mono">{formatIDR(w.final_amount)}</td>
                      <td className="px-4 py-3 text-right space-x-1.5">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" disabled={!isOwnerGM || busyId === w.id}
                          onClick={() => openAction("approve_wage", w.id, `Setujui fee ${w.staff_name} — ${formatIDR(w.final_amount)}`)}
                          data-testid={`btn-approve-wage-${w.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Setujui
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* AI Agent Pending */}
        <TabsContent value="agent" className="mt-0 space-y-3">
          {agentPending.length === 0 ? (
            <Card className="p-10 text-center text-sm text-[#8C9096] border border-[#E5E0D8] bg-white shadow-none">
              <Bot className="w-8 h-8 mx-auto mb-2 opacity-40"/>
              Tidak ada perubahan dari AI Agent yang menunggu persetujuan.
              <div className="text-[11px] mt-2 opacity-70">Perubahan sensitif dari AI (harga naik &gt;20%, unpublish, bulk &gt;5) akan muncul di sini otomatis.</div>
            </Card>
          ) : agentPending.map((ch) => {
            const isPending = ch.status === "pending_approval";
            const oldV = ch.old_value || {};
            const newV = ch.new_value || {};
            const fields = Array.from(new Set([...Object.keys(oldV), ...Object.keys(newV)]));
            return (
              <Card key={ch.id} className="p-4 border border-[#E5E0D8] bg-white shadow-none" data-testid={`agent-pending-${ch.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded font-semibold bg-[#F0EDE5] text-[#5C6066] border border-[#E5E0D8]">
                      <Bot className="w-3 h-3 inline -mt-0.5 mr-1"/> {ch.agent_name}
                    </span>
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded font-semibold bg-amber-50 text-amber-800 border border-amber-200">{ch.action}</span>
                    <span className="font-mono text-[10px] text-[#8C9096] truncate">{ch.resource_id}</span>
                  </div>
                  <div className="text-[10px] text-[#8C9096]">{(ch.created_at || "").slice(0, 19).replace("T", " ")}</div>
                </div>
                <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid={`agent-reason-${ch.id}`}>
                  <b>Alasan Butuh Approval:</b> {ch.reason || "-"}
                </div>
                <div className="mt-3 space-y-1">
                  {fields.map((k) => {
                    const ov = oldV[k]; const nv = newV[k];
                    const changed = JSON.stringify(ov) !== JSON.stringify(nv);
                    if (!changed) return null;
                    return (
                      <div key={k} className="grid grid-cols-1 md:grid-cols-[140px_1fr_20px_1fr] gap-2 items-start text-xs">
                        <span className="uppercase tracking-wider text-[10px] font-semibold text-[#8C9096]">{k}</span>
                        <div className="font-mono text-[#5C6066] bg-rose-50/40 border border-rose-100 rounded px-2 py-1 break-all">{ov === undefined || ov === null ? <em className="opacity-50">(kosong)</em> : String(typeof ov === "object" ? JSON.stringify(ov) : ov)}</div>
                        <ArrowRight className="w-3.5 h-3.5 text-[#8C9096] mt-1.5 mx-auto hidden md:block"/>
                        <div className="font-mono text-[#1E1611] bg-emerald-50/40 border border-emerald-100 rounded px-2 py-1 break-all">{nv === undefined || nv === null ? <em className="opacity-50">(kosong)</em> : String(typeof nv === "object" ? JSON.stringify(nv) : nv)}</div>
                      </div>
                    );
                  })}
                </div>
                {isPending && (
                  <div className="mt-3 flex justify-end gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs border-rose-300 text-rose-600 hover:bg-rose-50" disabled={!isOwnerGM || busyId === ch.id}
                      onClick={() => openAction("reject_agent", ch.id, `Tolak perubahan AI: ${ch.action}`, ch.reason)}
                      data-testid={`btn-agent-reject-${ch.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1"/>Tolak
                    </Button>
                    <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!isOwnerGM || busyId === ch.id}
                      onClick={() => openAction("approve_agent", ch.id, `Setujui perubahan AI: ${ch.action}`,
                        `${ch.reason} · Akan langsung menerapkan perubahan ke ${ch.resource_id}.`)}
                      data-testid={`btn-agent-approve-${ch.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Setujui & Terapkan
                    </Button>
                  </div>
                )}
                {!isPending && (
                  <div className="mt-3 text-[10px] text-[#8C9096] flex justify-end gap-2">
                    Status: <span className={`font-semibold ${ch.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>{ch.status}</span>
                    {ch.approved_by && <span>oleh {ch.approved_by}</span>}
                    {ch.rejected_by && <span>oleh {ch.rejected_by}</span>}
                  </div>
                )}
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Confirm Modal */}
      <Dialog open={!!actionDlg} onOpenChange={(v) => !v && setActionDlg(null)}>
        <DialogContent className="max-w-md" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle>{actionDlg?.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {actionDlg?.warn && <div className="text-xs p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800">{actionDlg.warn}</div>}
            <div>
              <Label className="text-xs">Catatan (opsional)</Label>
              <Textarea rows={3} className="mt-1.5" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan / catatan untuk log audit" data-testid="approval-note"/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDlg(null)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={submitAction} disabled={busyId === actionDlg?.id} data-testid="btn-confirm-action">Konfirmasi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Create Modal */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-lg" data-testid="refund-dialog">
          <DialogHeader><DialogTitle>Ajukan Refund Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Booking</Label>
              <Select value={rForm.booking_id} onValueChange={(v) => setRForm({ ...rForm, booking_id: v, payment_id: "" })}>
                <SelectTrigger className="mt-1.5" data-testid="refund-booking-select"><SelectValue placeholder="Pilih booking"/></SelectTrigger>
                <SelectContent className="max-h-72">
                  {bookings.filter((b) => b.status !== "Cancelled").slice(0, 200).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.booking_code} — {b.event_name || "-"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {rForm.booking_id && (
              <div>
                <Label>Pembayaran Terkait (opsional)</Label>
                <Select value={rForm.payment_id} onValueChange={(v) => setRForm({ ...rForm, payment_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="refund-payment-select"><SelectValue placeholder="Pilih payment (opsional)"/></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {payments.filter((p) => p.booking_id === rForm.booking_id && Number(p.amount) > 0).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.payment_type} · {formatDate(p.payment_date)} · {formatIDR(p.amount)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nominal Refund</Label><Input type="number" className="mt-1.5" value={rForm.amount} onChange={(e) => setRForm({ ...rForm, amount: e.target.value })} data-testid="refund-amount"/></div>
              <div><Label>Metode</Label>
                <Select value={rForm.method} onValueChange={(v) => setRForm({ ...rForm, method: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Transfer">Transfer</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="QRIS">QRIS</SelectItem>
                    <SelectItem value="Other">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Tgl Pengajuan</Label><Input type="date" className="mt-1.5" value={rForm.request_date} onChange={(e) => setRForm({ ...rForm, request_date: e.target.value })}/></div>
            </div>
            <div><Label>Alasan Refund</Label><Textarea rows={3} className="mt-1.5" value={rForm.reason} onChange={(e) => setRForm({ ...rForm, reason: e.target.value })} placeholder="Contoh: Klien batal, dobel bayar, kelebihan bayar" data-testid="refund-reason"/></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Batal</Button>
            <Button className="bg-brand text-white" onClick={submitRefund} disabled={!rForm.booking_id || !rForm.amount} data-testid="btn-submit-refund">Ajukan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
